import { useEffect, useState } from "react";
import { API, apiError, formatDateTime } from "../lib/api";
import { Plus, Trash2, Edit2, X, User } from "lucide-react";
import { toast } from "sonner";

const EMPTY = { email: "", password: "", name: "", role: "cashier", branch_id: "" };

export default function Users() {
  const [users, setUsers] = useState([]);
  const [branches, setBranches] = useState([]);
  const [modal, setModal] = useState(null);
  const [form, setForm] = useState(EMPTY);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    setLoading(true);
    try {
      const [u, b] = await Promise.all([API.get("/users"), API.get("/branches")]);
      setUsers(u.data); setBranches(b.data);
    } catch (e) { toast.error(apiError(e)); }
    finally { setLoading(false); }
  };
  useEffect(() => { load(); }, []);

  const openNew = () => { setForm(EMPTY); setModal("new"); };
  const openEdit = (u) => { setForm({ email: u.email, password: "", name: u.name, role: u.role, branch_id: u.branch_id || "" }); setModal(u); };

  const save = async (e) => {
    e.preventDefault();
    try {
      if (modal === "new") {
        await API.post("/users", { ...form, branch_id: form.branch_id || null });
        toast.success("User created");
      } else {
        const payload = { ...form };
        if (!payload.password) delete payload.password;
        delete payload.email;
        payload.branch_id = payload.branch_id || null;
        await API.put(`/users/${modal.id}`, payload);
        toast.success("User updated");
      }
      setModal(null); load();
    } catch (e) { toast.error(apiError(e)); }
  };

  const del = async (u) => {
    if (!window.confirm(`Delete user ${u.email}?`)) return;
    try { await API.delete(`/users/${u.id}`); toast.success("Deleted"); load(); }
    catch (e) { toast.error(apiError(e)); }
  };

  const branchName = (id) => branches.find((b) => b.id === id)?.name || "—";

  return (
    <div className="p-4 sm:p-6 md:p-8 space-y-5" data-testid="users-page">
      <div className="flex items-end justify-between flex-wrap gap-3">
        <div>
          <div className="overline mb-2">Access Control</div>
          <h1 className="font-display text-3xl font-bold">Users & Roles</h1>
          <p className="text-sm text-slate-500 mt-1">Create staff accounts and manage role permissions.</p>
        </div>
        <button onClick={openNew} className="btn-primary" data-testid="add-user-btn"><Plus className="w-4 h-4" /> New User</button>
      </div>

      <div className="bg-white border border-slate-200 rounded-xl overflow-hidden">
        {loading ? <div className="p-10 text-slate-500 text-sm text-center">Loading…</div> : (
          <div className="overflow-x-auto">
            <table className="data-table">
              <thead><tr><th>User</th><th>Role</th><th>Branch</th><th>Status</th><th>Joined</th><th className="text-right">Actions</th></tr></thead>
              <tbody>
                {users.map((u) => (
                  <tr key={u.id} data-testid={`user-row-${u.id}`}>
                    <td>
                      <div className="flex items-center gap-3">
                        <div className="w-9 h-9 rounded-full bg-slate-100 text-slate-500 flex items-center justify-center">
                          <User className="w-4 h-4" />
                        </div>
                        <div>
                          <div className="font-semibold">{u.name}</div>
                          <div className="text-xs text-slate-500">{u.email}</div>
                        </div>
                      </div>
                    </td>
                    <td><span className={`tag ${u.role === "admin" ? "tag-info" : u.role === "branch_head" ? "tag-warning" : u.role === "cashier" ? "tag-success" : "tag-muted"} capitalize`}>{u.role === "branch_head" ? "Branch Head" : u.role}</span></td>
                    <td>{branchName(u.branch_id)}</td>
                    <td>{u.active ? <span className="tag tag-success">Active</span> : <span className="tag tag-danger">Disabled</span>}</td>
                    <td className="text-xs text-slate-500">{formatDateTime(u.created_at)}</td>
                    <td className="text-right">
                      <div className="flex gap-1 justify-end">
                        <button onClick={() => openEdit(u)} className="p-1.5 rounded hover:bg-slate-100 text-slate-500 hover:text-[#002FA7]" data-testid={`edit-user-${u.id}`}><Edit2 className="w-3.5 h-3.5" /></button>
                        <button onClick={() => del(u)} className="p-1.5 rounded hover:bg-red-50 text-slate-500 hover:text-red-600" data-testid={`del-user-${u.id}`}><Trash2 className="w-3.5 h-3.5" /></button>
                      </div>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )}
      </div>

      {modal && (
        <div className="fixed inset-0 z-50 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4">
          <form onSubmit={save} className="bg-white rounded-xl shadow-2xl w-full max-w-md">
            <div className="p-5 border-b border-slate-200 flex items-center justify-between">
              <h3 className="font-display text-xl font-semibold">{modal === "new" ? "Create User" : "Edit User"}</h3>
              <button type="button" onClick={() => setModal(null)} className="p-1 hover:bg-slate-100 rounded"><X className="w-4 h-4" /></button>
            </div>
            <div className="p-5 space-y-3 text-sm">
              <L label="Name"><input required value={form.name} onChange={(e) => setForm({ ...form, name: e.target.value })} className="inp" /></L>
              <L label="Email"><input required type="email" value={form.email} disabled={modal !== "new"} onChange={(e) => setForm({ ...form, email: e.target.value })} className="inp disabled:bg-slate-50" /></L>
              <L label={modal === "new" ? "Password" : "New Password (leave blank to keep)"}>
                <input type="password" required={modal === "new"} value={form.password} onChange={(e) => setForm({ ...form, password: e.target.value })} className="inp" />
              </L>
              <L label="Role">
                <select value={form.role} onChange={(e) => setForm({ ...form, role: e.target.value })} className="inp">
                  <option value="admin">Admin</option><option value="branch_head">Branch Head</option><option value="cashier">Cashier</option><option value="accountant">Accountant</option>
                </select>
              </L>
              <L label="Branch">
                <select value={form.branch_id} onChange={(e) => setForm({ ...form, branch_id: e.target.value })} className="inp">
                  <option value="">— Unassigned —</option>
                  {branches.map((b) => <option key={b.id} value={b.id}>{b.name}</option>)}
                </select>
              </L>
            </div>
            <div className="p-5 border-t border-slate-200 flex justify-end gap-2">
              <button type="button" onClick={() => setModal(null)} className="btn-ghost">Cancel</button>
              <button type="submit" className="btn-primary" data-testid="save-user-btn">Save</button>
            </div>
          </form>
        </div>
      )}
      <style>{`.inp{width:100%;border:1px solid #E2E8F0;border-radius:8px;padding:.5rem .75rem;font-size:.875rem;outline:none}.inp:focus{border-color:#002FA7;box-shadow:0 0 0 3px rgba(0,47,167,.15)}`}</style>
    </div>
  );
}
function L({ label, children }) { return <label className="block"><div className="text-xs font-semibold text-slate-600 mb-1.5">{label}</div>{children}</label>; }
